import {
  Button,
  Center,
  Group,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { PaymentSteps } from 'api/models';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import Amount from 'shared/components/Amount';
import { useReconcilePaymentPlan } from 'shared/hooks/use-reconcile-payment-plan';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';

import styleIdentifiers from './PaymentPlanDetailTable.module.scss';

const styles = classNames.bind(styleIdentifiers);

type PaymentPlanDetailTableProps = {
  paymentSteps: PaymentSteps[];
  currency?: CurrencyCode;
  currentStep: number;
  refetchPaymentPlan: () => void;
};

type RadioGroupValue = 'fullAmount' | 'partialAmount';

export const PaymentPlanDetailTable = ({
  paymentSteps,
  currentStep,
  currency = 'EUR',
  refetchPaymentPlan,
}: PaymentPlanDetailTableProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const { reconcilePaymentPlan } = useReconcilePaymentPlan();

  const [radioGroupValue, setRadioGroupValue] = useState<RadioGroupValue>('partialAmount');
  const [isModalOpen, { open: openModal, close }] = useDisclosure(false);
  const [currentPaymentStep, setCurrentPaymentStep] = useState<Omit<PaymentSteps, 'type'>>();
  const [partialAmountValue, setPartialAmountValue] = useState<number>(0);

  const totalAmount = paymentSteps.reduce(
    (acc, { attributes: { amount } }) => acc + Number(amount),
    0,
  );
  const totalRemaining = paymentSteps.reduce(
    (acc, { attributes: { remaining_balance } }) => acc + Number(remaining_balance),
    0,
  );
  const totalPaid = totalAmount - totalRemaining;

  const firstUnpaidStepId = [...paymentSteps]
    .sort((a, b) => dayjs(a.attributes.due_date).diff(dayjs(b.attributes.due_date)))
    .find(step => Number(step.attributes.remaining_balance) > 0)?.id;

  const shouldDisplayReconciliation = company.manual_payment_plan_reconciliation;
  const isPartialAmountValid = partialAmountValue > 0;
  const canSubmit = radioGroupValue !== 'partialAmount' || isPartialAmountValid;

  const openReconciliationModal = (paymentStep: Omit<PaymentSteps, 'type'>) => {
    setCurrentPaymentStep(paymentStep);
    setPartialAmountValue(0);
    setRadioGroupValue('partialAmount');
    openModal();
  };

  const sendForm = () => {
    if (!currentPaymentStep) return;

    reconcilePaymentPlan(
      {
        id: currentPaymentStep.id as string,
        amount:
          radioGroupValue === 'partialAmount'
            ? partialAmountValue
            : currentPaymentStep.attributes.remaining_balance,
      },
      {
        onSuccess: () => {
          refetchPaymentPlan();
          close();
        },
      },
    );
  };

  const rows = paymentSteps.map(({ id, attributes }) => {
    const { amount, due_date, remaining_balance, days_late } = attributes;

    const isCurrentStep = currentStep === Number(id);
    const alreadyPaid = amount - remaining_balance;
    const hasRemainingBalance = remaining_balance > 0;

    return (
      <Table.Tr
        bg={isCurrentStep ? 'purple.0' : undefined}
        fw={isCurrentStep ? 'bold' : 'normal'}
        key={id}
      >
        <Table.Td>{dayjs(due_date).format('L')}</Table.Td>
        <Table.Td>{days_late ?? t(i18nKeys.PAYMENT_PLAN.UTILS.NO_NEXT_STEP)}</Table.Td>
        <Table.Td>
          <Amount localizedValue={amount} value={amount} suffix={currency} />
        </Table.Td>
        <Table.Td>
          <Text
            size="sm"
            c={alreadyPaid === Number(amount) ? 'validation.9' : undefined}
            fw={isCurrentStep ? 'bold' : 'normal'}
          >
            <Amount localizedValue={alreadyPaid} value={alreadyPaid} suffix={currency} />
          </Text>
        </Table.Td>
        <Table.Td>
          <Text
            size="sm"
            c={alreadyPaid !== Number(amount) ? 'warning' : undefined}
            fw={isCurrentStep ? 'bold' : 'normal'}
          >
            <Amount
              localizedValue={remaining_balance}
              value={remaining_balance}
              suffix={currency}
            />
          </Text>
        </Table.Td>
        {shouldDisplayReconciliation && (
          <Table.Td>
            {hasRemainingBalance && firstUnpaidStepId === id && (
              <Center>
                <Group gap={4}>
                  <Button
                    onClick={() => openReconciliationModal({ id, attributes })}
                    size="compact-sm"
                  >
                    {t(i18nKeys.PAYMENT_PLAN.MODAL.BUTTON_LABEL)}
                  </Button>
                </Group>
              </Center>
            )}
          </Table.Td>
        )}
      </Table.Tr>
    );
  });

  const footer = (
    <Table.Tr style={{ borderBottom: 'none' }}>
      <Table.Td />
      <Table.Td />
      <Table.Td>
        <Amount localizedValue={totalAmount} value={totalAmount} suffix={currency} />
      </Table.Td>
      <Table.Td>
        <Amount localizedValue={totalPaid} value={totalPaid} suffix={currency} />
      </Table.Td>
      <Table.Td>
        <Amount localizedValue={totalRemaining} value={totalRemaining} suffix={currency} />
      </Table.Td>
      {shouldDisplayReconciliation && <Table.Td />}
    </Table.Tr>
  );

  const header = (
    <Table.Tr>
      <Table.Th>{t(i18nKeys.DATE)}</Table.Th>
      <Table.Th>{t(i18nKeys.INVOICE.DAY_OF_DELAY)}</Table.Th>
      <Table.Th>{t(i18nKeys.TOTAL_VAT_PRICE)}</Table.Th>
      <Table.Th>{t(i18nKeys.INVOICING.ALREADY_PAID)}</Table.Th>
      <Table.Th>
        {t(i18nKeys.BALANCE_CURRENCY, {
          text: getCurrencySymbolFromCurrencyCode(currency),
        })}
      </Table.Th>
      {shouldDisplayReconciliation && <Table.Th />}
    </Table.Tr>
  );

  return (
    <>
      <Panel
        withBorder
        flushContent
        title={t(i18nKeys.PAYMENT_PLAN.ATTRIBUTES.PAYMENT_STEPS)}
        className={styles('payment-steps')}
      >
        <Table highlightOnHover highlightOnHoverColor="purple.1">
          <Table.Thead>{header}</Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
          {paymentSteps.length > 1 && <Table.Tfoot>{footer}</Table.Tfoot>}
        </Table>
      </Panel>
      <Modal
        size="auto"
        centered
        opened={isModalOpen}
        onClose={close}
        title={`${t(i18nKeys.PAYMENT_PLAN.MODAL.TITLE)} - ${dayjs(currentPaymentStep?.attributes.due_date).format('LL')}`}
        styles={{ body: { paddingBottom: 0 } }}
      >
        <Stack m="md">
          <Radio.Group
            value={radioGroupValue}
            name="reconciliation"
            withAsterisk
            onChange={(value: string) => setRadioGroupValue(value as RadioGroupValue)}
          >
            <Text mb="xl">{t(i18nKeys.PAYMENT_PLAN.MODAL.DESCRIPTION)}</Text>
            <Stack mt="xs">
              <Radio
                value="partialAmount"
                styles={{ body: { alignItems: 'center' } }}
                label={
                  <Group>
                    <Text size="sm">{t(i18nKeys.PAYMENT_PLAN.MODAL.PARTIAL_AMOUNT)}</Text>
                    <NumberInput
                      allowNegative={false}
                      placeholder={`0,00 ${getCurrencySymbolFromCurrencyCode(currency)}`}
                      suffix={getCurrencySymbolFromCurrencyCode(currency)}
                      max={currentPaymentStep?.attributes.remaining_balance}
                      onChange={value =>
                        setPartialAmountValue(typeof value === 'number' ? value : 0)
                      }
                      disabled={radioGroupValue !== 'partialAmount'}
                    />
                  </Group>
                }
              />
              <Radio
                value="fullAmount"
                styles={{ body: { alignItems: 'center' } }}
                label={
                  <Group>
                    <Text size="sm">{t(i18nKeys.PAYMENT_PLAN.MODAL.FULL_AMOUNT)}</Text>
                    <Text fw="bold">
                      {(currentPaymentStep?.attributes.remaining_balance ?? 0) +
                        getCurrencySymbolFromCurrencyCode(currency)}
                    </Text>
                  </Group>
                }
              />
            </Stack>
          </Radio.Group>
        </Stack>
        <Group p="md" bg="white" justify="flex-end" gap="md" w="100%" pos="sticky" bottom={0}>
          <Button variant="light" onClick={close}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button disabled={!canSubmit} onClick={sendForm}>
            {t(i18nKeys.PAYMENT_PLAN.MODAL.RECONCILE)}
          </Button>
        </Group>
      </Modal>
    </>
  );
};
